import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { experimentalStyled as styled } from '@mui/material/styles';
import {
  Box,
  Link,
  Drawer,
  Typography,
  Stack,
  Autocomplete,
  TextField,
  Tooltip
} from '@mui/material';
// components
import { useDispatch, useSelector } from 'react-redux';
import Avatar from 'react-avatar';
import ProfileCompletionBar from '../../components/ProfileComplitionBar';
import {
  ADD_NOTIFICATIONS,
  ADD_STUDENT,
  ADD_TEACHER,
  CHANGE_SELECTED_ORG,
  REPLACE_CLASSES,
  RESET_STATE
} from '../../redux/constants';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { MHidden } from '../../components/@material-extend';
//
import sidebarConfig from './SidebarConfig';
import studentSidebarConfig from './StudentSidebarConfig';
import teacherSidebarConfig from './TeacherSidebarConfig';
import Iconify from '../../fl-components/Iconify';
import palette from '../../theme/palette';
import {
  CalculateOrgProfilePercentage,
  CalculateUserProfilePercentage
} from '../../helper/commonHelper';
import { fetchClasses } from '../../service/class.service';
import { fetchTeacherByOrg } from '../../service/teacher.service';
import { fetchStudentByOrg } from '../../service/student.service';
import { getAllNotification } from '../../service/notification.service';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH
  }
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[200]
}));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const loggedInAs = useSelector((state) => state.auth.loggedInAs);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const orgDetails = useSelector((state) => state.org.orgDetails);
  const selectedOrg = useSelector((state) => state.org.selectedOrg);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  let sidebarData;
  if (loggedInAs === 'teacher') {
    sidebarData = teacherSidebarConfig;
    if (
      (selectedOrg?.id ?? selectedOrg?._id) &&
      selectedOrg.role === 'admin' &&
      sidebarData.filter((sd) => sd.title === 'calender').length === 0
    )
      sidebarData.splice(4, 0, {
        title: 'calender',
        path: '/dashboard/calender',
        icon: <Iconify icon="eva:calendar-fill" width={22} height={22} />
      });
  } else if (loggedInAs === 'student') {
    sidebarData = studentSidebarConfig;
    if (
      (selectedOrg?.id ?? selectedOrg?._id) &&
      sidebarData.filter((sd) => sd.title === 'calender').length === 0
    )
      sidebarData.splice(2, 0, {
        title: 'calender',
        path: '/dashboard/calender',
        icon: <Iconify icon="eva:calendar-fill" width={22} height={22} />
      });
  } else if (loggedInAs === 'admin') {
    sidebarData = sidebarConfig;
  } else {
    sidebarData = [];
  }

  const onOrgChange = async (newOrg) => {
    dispatch({ type: RESET_STATE });
    dispatch({ type: CHANGE_SELECTED_ORG, payload: newOrg });

    // Make api call to fetch the class list for the same
    const orgClasses = await fetchClasses(newOrg?.id ?? newOrg?._id ?? '');
    if (orgClasses) {
      dispatch({
        type: REPLACE_CLASSES,
        payload: {
          classes: orgClasses.results,
          offset: 2
        }
      });
    }

    // API call for get teacher and student for the same
    const teachRes = await fetchTeacherByOrg(newOrg?.id ?? newOrg?._id);
    if (teachRes) {
      dispatch({ type: ADD_TEACHER, payload: teachRes });
    }

    const studRes = await fetchStudentByOrg(selectedOrg?.id ?? selectedOrg?._id);
    if (studRes) {
      dispatch({ type: ADD_STUDENT, payload: studRes });
    }

    // Make call to fetch notifications
    const notiRes = await getAllNotification(selectedOrg?.id ?? selectedOrg?._id);
    if (notiRes) {
      dispatch({
        type: ADD_NOTIFICATIONS,
        payload: {
          data: notiRes.results,
          offset: 2
        }
      });
    }
  };

  // Personal profile complete number
  const userCompletedProfile = useMemo(
    () => CalculateUserProfilePercentage(currentUser),
    [currentUser]
  );

  // Org profile complete number
  const orgCompletedProfile = useMemo(() => {
    if (currentUser?.profile === 'admin') {
      return CalculateOrgProfilePercentage(selectedOrg);
    }
    return 0;
  }, [selectedOrg]);

  /**
   * Check if details are fields or not.
   * @returns {boolean} is detail field
   */
  const isAllDetailFiled = () => {
    if (currentUser?.profile === 'student' && userCompletedProfile === 100) {
      return true;
    }
    if (userCompletedProfile === 100 && orgCompletedProfile === 100) {
      return true;
    }
    return false;
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': { height: '100%', display: 'flex', flexDirection: 'column' }
      }}
    >
      <Box sx={{ mb: 5, mx: 2.5, marginTop: 5 }}>
        <Link underline="none" component={RouterLink} to="#">
          <AccountStyle>
            {/* <Avatar src={account.photoURL} alt="photoURL" /> */}
            <Avatar name={currentUser?.fullName} src={currentUser?.profileImage} round size={45} />
            <Box sx={{ ml: 2, width: '100%' }}>
              <Typography
                variant="subtitle2"
                sx={{ color: 'text.primary' }}
                display="flex"
                alignItems="center"
                gap={0.5}
              >
                {currentUser?.fullName}
                {isAllDetailFiled() && (
                  <Tooltip title="Profile Completed" placement="top" arrow>
                    <Box display="flex" alignItems="center">
                      <Iconify
                        icon="material-symbols:verified-rounded"
                        color={palette.primary.main}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Typography>
              {!isAllDetailFiled() && (
                <Box sx={{ width: '100%' }}>
                  <ProfileCompletionBar onCloseSidebar={onCloseSidebar} />
                </Box>
              )}
              {/* <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {account.role}
              </Typography> */}
            </Box>
          </AccountStyle>
        </Link>
      </Box>

      {orgDetails.length > 0 && (
        <Box sx={{ mb: 5, mx: 2.5 }}>
          <Autocomplete
            options={orgDetails}
            disabled={orgDetails.length === 1}
            disableClearable
            value={selectedOrg}
            getOptionLabel={(option) => option.name}
            sx={{ maxWidth: 240, minWidth: 240 }}
            onChange={(e, newValue) => {
              onOrgChange(newValue);
            }}
            renderInput={(params) => <TextField {...params} label="Select Organization" />}
          />
        </Box>
      )}

      <NavSection navConfig={sidebarData} />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 3, pb: 5, mt: 10 }}>
        <Stack alignItems="center" spacing={3}>
          <Box component="img" src="/static/logo-new.svg" />
        </Stack>
        <Link component={RouterLink} to="#" style={{ textAlign: 'center' }}>
          <Typography> Support Ticket </Typography>
        </Link>
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default'
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
